import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, Server, Radio, GitBranch, FlaskConical, Box,
  ExternalLink, Plus, X, CheckCircle2, Loader2, Upload,
  Video, Users, Linkedin, Globe, ChevronRight,
  Layers, Search, ArrowRight
} from 'lucide-react';

const CATEGORIES = [
  { id: 'all', label: 'All Projects', icon: Layers },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'infrastructure', label: 'Infrastructure', icon: Server },
  { id: 'communication', label: 'Communication Protocols', icon: Radio },
  { id: 'coordination', label: 'Coordination Protocols', icon: GitBranch },
  { id: 'testbed', label: 'Testbeds', icon: FlaskConical },
  { id: 'sandbox', label: 'Sandboxes', icon: Box }
];

const SEED_PROJECTS = [
  { id: 'agentfacts', name: 'AgentFacts', category: 'security', description: 'Cryptographically verified identity and capability attestation for autonomous AI agents. The trust layer that makes agent-to-agent authentication possible across the agentic web.', logoUrl: '', websiteUrl: 'https://list39.org', creators: [{ name: 'Project NANDA', linkedin: '' }], verified: true, featured: true },
  { id: 'nanda-index', name: 'NANDA Index', category: 'infrastructure', description: 'The global discovery and resolution system for AI agents. DNS for the agentic web. Enables agents to find, authenticate, and connect with each other across the internet.', logoUrl: '', websiteUrl: 'https://projectnanda.org', creators: [{ name: 'Project NANDA', linkedin: '' }], verified: true, featured: true },
  { id: 'a2a-protocol', name: 'A2A Protocol', category: 'communication', description: 'Standardized agent-to-agent communication protocol enabling seamless message exchange, task delegation, and collaborative workflows between heterogeneous AI agents.', logoUrl: '', websiteUrl: '', creators: [{ name: 'Google', linkedin: '' }], verified: true, featured: true },
  { id: 'openclaw', name: 'OpenClaw', category: 'coordination', description: 'Open-source agent orchestration platform for creating, deploying, and coordinating multi-agent systems. Build complex agent workflows with visual pipelines.', logoUrl: '', websiteUrl: 'https://openclaw.org', creators: [{ name: 'OpenClaw Team', linkedin: '' }], verified: true },
  { id: 'list39-registry', name: 'List39', category: 'infrastructure', description: 'Decentralized agent registry and discovery service. Register your agent, publish AgentFacts, and make your AI discoverable across the NANDA ecosystem.', logoUrl: '', websiteUrl: 'https://list39.org', creators: [{ name: 'Project NANDA', linkedin: '' }], verified: true },
  { id: 'mcp-registry', name: 'NANDA MCP Registry', category: 'infrastructure', description: 'Curated registry of Model Context Protocol servers integrated with the NEST infrastructure. Extend agent capabilities with standardized tool access.', logoUrl: '', websiteUrl: 'https://nest.projectnanda.org', creators: [{ name: 'Project NANDA', linkedin: '' }], verified: true },
  { id: 'clawbot-framework', name: 'ClawBot Framework', category: 'testbed', description: 'Deploy, test, and benchmark AI agents in controlled environments. Pre-configured agent personalities, automated evaluation, and performance metrics.', logoUrl: '', websiteUrl: '', creators: [{ name: 'NEST Labs', linkedin: '' }], verified: true },
  { id: 'agent-mesh', name: 'Agent Mesh Network', category: 'communication', description: 'Peer-to-peer communication mesh for low-latency agent interactions. Supports real-time streaming, event-driven messaging, and fault-tolerant routing.', logoUrl: '', websiteUrl: '', creators: [{ name: 'Mesh Protocol Team', linkedin: '' }], verified: true },
  { id: 'nest-sandbox', name: 'NEST Sandbox', category: 'sandbox', description: 'Isolated execution environment for testing agent behaviors, validating protocols, and simulating multi-agent scenarios before production deployment.', logoUrl: '', websiteUrl: '', creators: [{ name: 'Project NANDA', linkedin: '' }], verified: true },
  { id: 'agent-guard', name: 'AgentGuard', category: 'security', description: 'Runtime security monitoring and threat detection for autonomous agents. Behavioral analysis, anomaly detection, and automated containment protocols.', logoUrl: '', websiteUrl: '', creators: [{ name: 'Security Working Group', linkedin: '' }], verified: true },
  { id: 'slim-protocol', name: 'SLIM Protocol', category: 'coordination', description: 'Lightweight coordination protocol for resource-constrained agent environments. Minimal overhead task scheduling, consensus, and state synchronization.', logoUrl: '', websiteUrl: '', creators: [{ name: 'SLIM Consortium', linkedin: '' }], verified: true },
  { id: 'nest-telemetry', name: 'NEST Telemetry', category: 'infrastructure', description: 'Real-time observability platform for the agentic web. Distributed tracing, performance metrics, and health monitoring across agent networks.', logoUrl: '', websiteUrl: '', creators: [{ name: 'Project NANDA', linkedin: '' }], verified: true }
];

const getCategoryIcon = (category) => {
  const icons = { security: Shield, infrastructure: Server, communication: Radio, coordination: GitBranch, testbed: FlaskConical, sandbox: Box };
  return icons[category] || Layers;
};

const Ecosystem = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeCategory, setActiveCategory] = useState('all');
  const [projects, setProjects] = useState(SEED_PROJECTS);
  const [showForm, setShowForm] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '', category: 'infrastructure', description: '', logoUrl: '', videoUrl: '', websiteUrl: '',
    creators: [{ name: '', linkedin: '' }]
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3001' : '');
        const response = await fetch(`${API_BASE}/api/ecosystem/projects`);
        if (response.ok) {
          const data = await response.json();
          if (data.projects && data.projects.length > 0) {
            const seedIds = SEED_PROJECTS.map(p => p.id);
            const newProjects = data.projects.filter(p => !seedIds.includes(p.id));
            setProjects([...SEED_PROJECTS, ...newProjects]);
          }
        }
      } catch (err) { /* use seed */ }
    };
    fetchProjects();
  }, []);

  const filteredProjects = projects.filter(p => {
    const matchesCategory = activeCategory === 'all' || p.category === activeCategory;
    const matchesSearch = !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const addCreator = () => setFormData(prev => ({ ...prev, creators: [...prev.creators, { name: '', linkedin: '' }] }));
  const updateCreator = (index, field, value) => {
    setFormData(prev => {
      const creators = [...prev.creators];
      creators[index] = { ...creators[index], [field]: value };
      return { ...prev, creators };
    });
  };
  const removeCreator = (index) => {
    if (formData.creators.length > 1) setFormData(prev => ({ ...prev, creators: prev.creators.filter((_, i) => i !== index) }));
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.description) return;
    setVerifying(true);
    try {
      const API_BASE = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3001' : '');
      await fetch(`${API_BASE}/api/ecosystem/submit`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(formData) });
    } catch (err) { /* continue */ }
    setTimeout(() => {
      setVerifying(false);
      setVerified(true);
      setProjects(prev => [{ id: 'user-' + Date.now(), ...formData, verified: true, creators: formData.creators.filter(c => c.name) }, ...prev]);
      setTimeout(() => {
        setVerified(false);
        setShowForm(false);
        setFormData({ name: '', category: 'infrastructure', description: '', logoUrl: '', videoUrl: '', websiteUrl: '', creators: [{ name: '', linkedin: '' }] });
      }, 3000);
    }, 30000);
  }, [formData]);

  const categoryStats = CATEGORIES.filter(c => c.id !== 'all').map(cat => ({ ...cat, count: projects.filter(p => p.category === cat.id).length }));

  const inputStyle = {
    width: '100%', padding: '12px 14px', background: '#111', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '6px', color: '#fff', fontSize: '0.85rem', outline: 'none', fontFamily: 'inherit'
  };

  return (
    <div style={{ background: '#000', minHeight: '100vh' }}>

      {/* Hero */}
      <div style={{ padding: isMobile ? '100px 20px 48px' : '110px 64px 64px', textAlign: 'center' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          padding: '6px 14px', border: '1px solid rgba(122,32,32,0.3)', borderRadius: '4px',
          fontSize: '0.7rem', color: '#7a2020', fontWeight: '600', letterSpacing: '0.08em',
          textTransform: 'uppercase', marginBottom: '24px'
        }}>
          <Layers size={12} /> The NEST Ecosystem
        </span>

        <h1 style={{
          fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: '600', color: '#fff',
          marginBottom: '16px', letterSpacing: '-0.03em', lineHeight: '1.15',
          maxWidth: '800px', margin: '0 auto 16px'
        }}>
          Building on <span style={{ color: '#7a2020' }}>NEST</span>
        </h1>

        <p style={{
          fontSize: 'clamp(0.9rem, 1.3vw, 1rem)', color: '#666',
          maxWidth: '600px', margin: '0 auto 32px', lineHeight: '1.7'
        }}>
          Protocols, platforms, and projects powering the agentic web, all built on NEST infrastructure.
        </p>

        {/* Category pills */}
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px', maxWidth: '800px', margin: '0 auto 28px' }}>
          {categoryStats.map(cat => (
            <div key={cat.id} style={{
              padding: '5px 12px', background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: '4px', fontSize: '0.75rem', color: '#666', fontWeight: '500',
              display: 'flex', alignItems: 'center', gap: '5px'
            }}>
              <cat.icon size={11} style={{ color: '#7a2020' }} />
              {cat.label}
              <span style={{ color: '#444', fontSize: '0.7rem' }}>{cat.count}</span>
            </div>
          ))}
        </div>

        <button
          onClick={() => setShowForm(true)}
          style={{
            padding: '12px 28px', background: '#7a2020', border: 'none', borderRadius: '6px',
            color: '#fff', fontSize: '0.875rem', fontWeight: '500', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: '8px'
          }}
        >
          <Plus size={16} /> Build on NEST
        </button>
      </div>

      {/* Filters & Grid */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '0 20px 32px' : '0 64px 48px' }}>

        {/* Filter bar */}
        <div style={{
          display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '12px',
          alignItems: isMobile ? 'stretch' : 'center', marginBottom: '24px'
        }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: isMobile ? '100%' : '360px' }}>
            <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#444' }} />
            <input
              type="text" placeholder="Search projects..." value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ ...inputStyle, paddingLeft: '36px' }}
              onFocus={(e) => e.target.style.borderColor = 'rgba(122,32,32,0.4)'}
              onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
            />
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', flex: 1 }}>
            {CATEGORIES.map(cat => (
              <button key={cat.id} onClick={() => setActiveCategory(cat.id)} style={{
                padding: '7px 14px',
                background: activeCategory === cat.id ? 'rgba(122,32,32,0.15)' : 'transparent',
                border: `1px solid ${activeCategory === cat.id ? 'rgba(122,32,32,0.4)' : 'rgba(255,255,255,0.06)'}`,
                borderRadius: '4px',
                color: activeCategory === cat.id ? '#fff' : '#666',
                fontSize: '0.75rem', fontWeight: '500', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '5px'
              }}>
                <cat.icon size={12} />
                {isMobile ? cat.label.split(' ')[0] : cat.label}
              </button>
            ))}
          </div>
        </div>

        <p style={{ fontSize: '0.8rem', color: '#444', marginBottom: '20px' }}>
          {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''}
          {activeCategory !== 'all' && ` in ${CATEGORIES.find(c => c.id === activeCategory)?.label}`}
        </p>

        {/* Projects Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(360px, 1fr))',
          gap: '12px'
        }}>
          {filteredProjects.map((project) => {
            const CatIcon = getCategoryIcon(project.category);
            return (
              <div key={project.id} style={{
                background: '#0a0a0a', borderRadius: '8px', padding: '24px',
                border: '1px solid rgba(255,255,255,0.06)',
                display: 'flex', flexDirection: 'column', position: 'relative',
                transition: 'border-color 0.2s'
              }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(122,32,32,0.25)'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'}
              >
                {project.featured && (
                  <span style={{
                    position: 'absolute', top: '14px', right: '14px',
                    padding: '3px 8px', border: '1px solid rgba(122,32,32,0.3)',
                    borderRadius: '3px', fontSize: '0.65rem', color: '#7a2020',
                    fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em'
                  }}>Core</span>
                )}

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
                  <div style={{
                    width: '40px', height: '40px', borderRadius: '6px',
                    background: 'rgba(122,32,32,0.08)', border: '1px solid rgba(122,32,32,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                  }}>
                    {project.logoUrl ? (
                      <img src={project.logoUrl} alt={project.name} style={{ width: '24px', height: '24px', objectFit: 'contain', borderRadius: '3px' }} />
                    ) : (
                      <CatIcon size={18} style={{ color: '#7a2020' }} />
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '0.95rem', fontWeight: '500', color: '#fff', marginBottom: '4px' }}>{project.name}</h3>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '3px',
                      padding: '2px 8px', background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.06)', borderRadius: '3px',
                      fontSize: '0.65rem', color: '#555', fontWeight: '500',
                      textTransform: 'uppercase', letterSpacing: '0.03em'
                    }}>
                      <CatIcon size={9} /> {project.category}
                    </span>
                  </div>
                </div>

                <p style={{ fontSize: '0.8rem', color: '#666', lineHeight: '1.6', marginBottom: '16px', flex: 1 }}>
                  {project.description}
                </p>

                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.04)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {project.creators && project.creators.length > 0 && (
                      <>
                        <Users size={12} style={{ color: '#444' }} />
                        <span style={{ fontSize: '0.75rem', color: '#555' }}>
                          {project.creators.map(c => c.name).join(', ')}
                        </span>
                      </>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    {project.videoUrl && (
                      <a href={project.videoUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#444', display: 'flex' }}>
                        <Video size={13} />
                      </a>
                    )}
                    {project.websiteUrl && (
                      <a href={project.websiteUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#444', display: 'flex' }}>
                        <ExternalLink size={13} />
                      </a>
                    )}
                    {project.verified && <CheckCircle2 size={13} style={{ color: '#3d8c5c' }} />}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredProjects.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#444' }}>
            <Layers size={40} style={{ marginBottom: '12px', opacity: 0.3 }} />
            <p style={{ fontSize: '0.9rem', marginBottom: '6px' }}>No projects found</p>
            <p style={{ fontSize: '0.8rem' }}>Try a different category or search term</p>
          </div>
        )}
      </div>

      {/* CTA */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '40px 20px 60px' : '60px 64px 80px' }}>
        <div style={{
          background: '#0a0a0a', border: '1px solid rgba(122,32,32,0.2)',
          borderRadius: '8px', padding: isMobile ? '36px 24px' : '48px', textAlign: 'center'
        }}>
          <h2 style={{ fontSize: 'clamp(1.3rem, 3vw, 1.75rem)', fontWeight: '600', color: '#fff', marginBottom: '12px' }}>
            Building something on NEST?
          </h2>
          <p style={{ fontSize: '0.85rem', color: '#666', maxWidth: '500px', margin: '0 auto 24px', lineHeight: '1.6' }}>
            If it runs on NEST infrastructure, we want to showcase it.
          </p>
          <button onClick={() => setShowForm(true)} style={{
            padding: '12px 28px', background: '#7a2020', border: 'none', borderRadius: '6px',
            color: '#fff', fontSize: '0.875rem', fontWeight: '500', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: '8px'
          }}>
            Submit Your Project <ArrowRight size={16} />
          </button>
        </div>
      </div>

      {/* Submission Modal */}
      {showForm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
          backdropFilter: 'blur(4px)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
        }} onClick={(e) => { if (e.target === e.currentTarget && !verifying) setShowForm(false); }}>
          <div style={{
            background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '8px', padding: isMobile ? '28px 20px' : '36px',
            maxWidth: '560px', width: '100%', maxHeight: '90vh', overflowY: 'auto', position: 'relative'
          }}>
            {/* Verifying */}
            {verifying && (
              <div style={{
                position: 'absolute', inset: 0, background: '#0a0a0a', borderRadius: '8px',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                zIndex: 10, padding: '40px'
              }}>
                <Loader2 size={36} style={{ color: '#7a2020', marginBottom: '20px', animation: 'spin 1s linear infinite' }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '500', color: '#fff', marginBottom: '8px' }}>Verifying Submission</h3>
                <p style={{ fontSize: '0.8rem', color: '#666', textAlign: 'center', lineHeight: '1.6' }}>
                  Reviewing project details, validating infrastructure compatibility, checking protocol compliance...
                </p>
                <div style={{ marginTop: '24px', width: '100%', height: '3px', background: 'rgba(255,255,255,0.04)', borderRadius: '2px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', background: '#7a2020', borderRadius: '2px', animation: 'progress 30s ease-in-out forwards' }} />
                  <style>{`@keyframes progress { 0% { width: 0%; } 100% { width: 100%; } }`}</style>
                </div>
              </div>
            )}

            {/* Verified */}
            {verified && (
              <div style={{
                position: 'absolute', inset: 0, background: '#0a0a0a', borderRadius: '8px',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                zIndex: 10, padding: '40px'
              }}>
                <CheckCircle2 size={44} style={{ color: '#3d8c5c', marginBottom: '20px' }} />
                <h3 style={{ fontSize: '1.2rem', fontWeight: '600', color: '#fff', marginBottom: '8px' }}>Verified & Listed</h3>
                <p style={{ fontSize: '0.85rem', color: '#666', textAlign: 'center', lineHeight: '1.6' }}>
                  Your project has been verified and added to the NEST ecosystem.
                </p>
              </div>
            )}

            {!verifying && !verified && (
              <button onClick={() => setShowForm(false)} style={{
                position: 'absolute', top: '16px', right: '16px', background: 'rgba(255,255,255,0.04)',
                border: 'none', borderRadius: '4px', color: '#555', padding: '6px', cursor: 'pointer', display: 'flex'
              }}><X size={18} /></button>
            )}

            {!verifying && !verified && (
              <>
                <h2 style={{ fontSize: '1.2rem', fontWeight: '600', color: '#fff', marginBottom: '6px' }}>Submit Your Project</h2>
                <p style={{ fontSize: '0.8rem', color: '#666', marginBottom: '28px' }}>Tell us about what you're building on NEST infrastructure.</p>

                <form onSubmit={handleSubmit}>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '500', color: '#fff', marginBottom: '6px' }}>Project Name *</label>
                    <input type="text" required value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g., AgentShield" style={inputStyle}
                      onFocus={(e) => e.target.style.borderColor = 'rgba(122,32,32,0.4)'} onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.08)'} />
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '500', color: '#fff', marginBottom: '6px' }}>Category *</label>
                    <select value={formData.category} onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}
                      onFocus={(e) => e.target.style.borderColor = 'rgba(122,32,32,0.4)'} onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}>
                      {CATEGORIES.filter(c => c.id !== 'all').map(cat => (<option key={cat.id} value={cat.id}>{cat.label}</option>))}
                    </select>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '500', color: '#fff', marginBottom: '6px' }}>Description *</label>
                    <textarea required value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} placeholder="What does your project do?" rows={3} style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }}
                      onFocus={(e) => e.target.style.borderColor = 'rgba(122,32,32,0.4)'} onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.08)'} />
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.8rem', fontWeight: '500', color: '#fff', marginBottom: '6px' }}>
                      <Upload size={12} /> Logo URL <span style={{ color: '#444', fontWeight: '400', fontSize: '0.7rem' }}>Optional</span>
                    </label>
                    <input type="url" value={formData.logoUrl} onChange={(e) => setFormData(prev => ({ ...prev, logoUrl: e.target.value }))} placeholder="https://..." style={inputStyle}
                      onFocus={(e) => e.target.style.borderColor = 'rgba(122,32,32,0.4)'} onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.08)'} />
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.8rem', fontWeight: '500', color: '#fff', marginBottom: '6px' }}>
                      <Video size={12} /> Video URL <span style={{ color: '#444', fontWeight: '400', fontSize: '0.7rem' }}>Optional, 2 min max</span>
                    </label>
                    <input type="url" value={formData.videoUrl} onChange={(e) => setFormData(prev => ({ ...prev, videoUrl: e.target.value }))} placeholder="https://..." style={inputStyle}
                      onFocus={(e) => e.target.style.borderColor = 'rgba(122,32,32,0.4)'} onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.08)'} />
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.8rem', fontWeight: '500', color: '#fff', marginBottom: '6px' }}>
                      <Globe size={12} /> Website <span style={{ color: '#444', fontWeight: '400', fontSize: '0.7rem' }}>Optional</span>
                    </label>
                    <input type="url" value={formData.websiteUrl} onChange={(e) => setFormData(prev => ({ ...prev, websiteUrl: e.target.value }))} placeholder="https://..." style={inputStyle}
                      onFocus={(e) => e.target.style.borderColor = 'rgba(122,32,32,0.4)'} onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.08)'} />
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.8rem', fontWeight: '500', color: '#fff', marginBottom: '10px' }}>
                      <Users size={12} /> Creators <span style={{ color: '#444', fontWeight: '400', fontSize: '0.7rem' }}>Optional</span>
                    </label>
                    {formData.creators.map((creator, i) => (
                      <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
                        <input type="text" value={creator.name} onChange={(e) => updateCreator(i, 'name', e.target.value)} placeholder="Name" style={{ ...inputStyle, flex: 1 }}
                          onFocus={(e) => e.target.style.borderColor = 'rgba(122,32,32,0.4)'} onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.08)'} />
                        <input type="url" value={creator.linkedin} onChange={(e) => updateCreator(i, 'linkedin', e.target.value)} placeholder="LinkedIn URL" style={{ ...inputStyle, flex: 1.5 }}
                          onFocus={(e) => e.target.style.borderColor = 'rgba(122,32,32,0.4)'} onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.08)'} />
                        {formData.creators.length > 1 && (
                          <button type="button" onClick={() => removeCreator(i)} style={{ background: 'rgba(122,32,32,0.1)', border: 'none', borderRadius: '4px', color: '#7a2020', padding: '8px', cursor: 'pointer', display: 'flex', flexShrink: 0 }}>
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                    <button type="button" onClick={addCreator} style={{
                      background: 'transparent', border: '1px dashed rgba(255,255,255,0.08)', borderRadius: '6px',
                      color: '#555', padding: '8px', cursor: 'pointer', fontSize: '0.75rem', width: '100%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px'
                    }}>
                      <Plus size={12} /> Add Creator
                    </button>
                  </div>

                  <button type="submit" style={{
                    width: '100%', padding: '14px', background: '#7a2020', border: 'none', borderRadius: '6px',
                    color: '#fff', fontSize: '0.875rem', fontWeight: '500', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                  }}>
                    Submit for Verification <ChevronRight size={16} />
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Ecosystem;
